import React, { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { collection, doc, onSnapshot, serverTimestamp, updateDoc } from "firebase/firestore";
import {
  FaBoxOpen,
  FaArchive,
  FaChartBar,
  FaClipboardCheck,
  FaClipboardList,
  FaComments,
  FaFileAlt,
  FaFileInvoiceDollar,
  FaFolderOpen,
  FaHistory,
  FaMapMarkerAlt,
  FaShieldAlt,
  FaSlidersH,
  FaUserCircle,
  FaUsers,
} from "react-icons/fa";
import { auth, db, startPerformanceTrace } from "./firebase";

function lazyWithTrace(moduleName, importer) {
  return lazy(async () => {
    const tracePromise = startPerformanceTrace("module_chunk_load", { module: moduleName });
    const modulePromise = importer();
    const [activeTrace, loadedModule] = await Promise.all([tracePromise, modulePromise]);
    activeTrace?.stop();
    return loadedModule;
  });
}

const AdminPanel = lazyWithTrace("admin_panel", () => import("./AdminPanel"));
const BackupModule = lazyWithTrace("backup", () => import("./BackupModule"));
const BillingModule = lazyWithTrace("billing", () => import("./BillingModule"));
const ClientCommunications = lazyWithTrace("client_communications", () => import("./ClientCommunications"));
const ClientDocuments = lazyWithTrace("client_documents", () => import("./ClientDocuments"));
const ClientMaster = lazyWithTrace("client_master", () => import("./ClientMaster"));
const FinishSignIn = lazyWithTrace("finish_sign_in", () => import("./FinishSignIn"));
const LegacyModuleFrame = lazyWithTrace("legacy_module_frame", () => import("./LegacyModuleFrame"));
const VermaLog = lazyWithTrace("verma_log", () => import("./Logs"));
const LoginForm = lazyWithTrace("login", () => import("./Login"));
const MasterPortal = lazyWithTrace("master_portal", () => import("./MasterPortal"));
const RegisterByCompany = lazyWithTrace("register_by_company", () => import("./RegisterByCompany"));
const RocIntelligence = lazyWithTrace("roc_intelligence", () => import("./RocIntelligence"));
const StationeryModule = lazyWithTrace("stationery", () => import("./StationeryModule"));
const WorkLog = lazyWithTrace("worklog", () => import("./WorkLog"));

function ModuleLoader({ label = "Loading module..." }) {
  return <div style={{ textAlign: "center", paddingTop: 60 }}>{label}</div>;
}

function isElevatedRole(role) {
  return ["admin", "superuser", "supervisor", "admi"].includes(role);
}

function isAdminRole(role) {
  return role === "admin";
}

function App() {
  const [user, setUser] = useState(null);
  const [userDoc, setUserDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRegister, setShowRegister] = useState(false);
  const [currentModule, setCurrentModule] = useState("hub");
  const [userDirectory, setUserDirectory] = useState([]);
  const [clientDirectory, setClientDirectory] = useState([]);
  const [dropdownMasters, setDropdownMasters] = useState({});
  const [misModuleData, setMisModuleData] = useState(null);
  const [clientDraft, setClientDraft] = useState(null);

  useEffect(() => {
    let userUnsub = () => {};
    const authUnsub = onAuthStateChanged(auth, (currentUser) => {
      setLoading(true);
      setUser(currentUser);
      userUnsub();

      if (!currentUser) {
        setUserDoc(null);
        setLoading(false);
        return;
      }

      userUnsub = onSnapshot(
        doc(db, "users", currentUser.uid),
        (snap) => {
          setUserDoc(snap.exists() ? { id: snap.id, ...snap.data() } : null);
          setLoading(false);
        },
        (error) => {
          console.error("Error fetching user profile:", error);
          setLoading(false);
        }
      );
    });

    return () => {
      authUnsub();
      userUnsub();
    };
  }, []);

  useEffect(() => {
    const needsDropdownMasters = ["assessments", "tasks", "hr", "visit", "documents", "billing", "receipts", "billingreports", "mis", "masters"].includes(currentModule);
    if (!user || !needsDropdownMasters) {
      setDropdownMasters({});
      return undefined;
    }
    const unsub = onSnapshot(doc(db, "app_config", "dropdown_masters"), (snap) => {
      setDropdownMasters(snap.exists() ? (snap.data().modules || {}) : {});
    });
    return () => unsub();
  }, [currentModule, user]);

  useEffect(() => {
    if (!user || !userDoc) {
      setMisModuleData(null);
      return undefined;
    }
    if (isElevatedRole(userDoc.role)) {
      setMisModuleData(null);
      return undefined;
    }
    const unsub = onSnapshot(doc(db, "legacy_modules", "mis"), (snap) => {
      setMisModuleData(snap.exists() ? (snap.data().data || {}) : null);
    });
    return () => unsub();
  }, [user, userDoc]);

  useEffect(() => {
    const needsClients = ["assessments", "tasks", "documents", "clientdocs", "communications", "billing", "receipts", "billingreports", "mis", "visit"].includes(currentModule);
    if (!user || !needsClients) {
      setClientDirectory([]);
      return undefined;
    }
    const unsub = onSnapshot(
      collection(db, "varma_clients"),
      (snap) => {
        setClientDirectory(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (error) => {
        console.error("Error fetching client directory:", error);
        setClientDirectory([]);
      }
    );
    return () => unsub();
  }, [currentModule, user]);

  useEffect(() => {
    const needsUsers = ["tasks", "documents", "communications", "mis"].includes(currentModule);
    if (!user || !needsUsers) {
      setUserDirectory([]);
      return undefined;
    }
    const unsub = onSnapshot(
      collection(db, "users"),
      (snap) => {
        setUserDirectory(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (error) => {
        console.error("Error fetching user directory:", error);
        setUserDirectory([]);
      }
    );
    return () => unsub();
  }, [currentModule, user]);

  const handleLogout = async () => {
    const { recordAuditEvent } = await import("./auditLog");
    await recordAuditEvent({ eventType: "sign-out", user, summary: "User signed out" });
    await signOut(auth);
    setCurrentModule("hub");
  };

  const hasAccess = useCallback((moduleKey) => {
    if (!userDoc) return false;
    if (moduleKey === "master") return false;
    if (moduleKey === "backup") return isAdminRole(userDoc.role);
    if (moduleKey === "vermalog") return isElevatedRole(userDoc.role);
    const role = userDoc.role;
    const permissions = userDoc.permissions || {};
    if (moduleKey === "masters") return isElevatedRole(role);

    if (isElevatedRole(role)) return true;
    if (moduleKey === "mis") {
      const name = userDoc.displayName || userDoc.name || user.displayName || user.email || "";
      const assignedSections = Object.values(misModuleData?.fieldAssignments || {}).flat();
      const assignedDue = (misModuleData?.dueDates || []).some((d) => (d.assignedTo || d.filedBy) === name);
      const assignedWork = (misModuleData?.assignments || []).some((a) => a.assignedTo === name);
      return assignedSections.includes(name) || assignedDue || assignedWork;
    }
    if (role === "custom") return moduleKey !== "master" && permissions[moduleKey] === true;
    if (moduleKey === "worklog" || moduleKey === "stationery") return true;
    if (moduleKey === "clients" || moduleKey === "visit" || moduleKey === "documents" || moduleKey === "clientdocs" || moduleKey === "communications") return true;
    if (role === "hr") return ["tasks", "stationery", "assessment", "hr"].includes(moduleKey);
    if (role === "user") return ["assessment", "tasks", "stationery"].includes(moduleKey);
    return false;
  }, [misModuleData, user, userDoc]);

  useEffect(() => {
    const handler = (event) => {
      if (event.origin !== window.location.origin || event.data?.type !== "portal-navigate") return;
      if (event.data.module && hasAccess(event.data.module)) setCurrentModule(event.data.module);
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [hasAccess]);

  useEffect(() => {
    startPerformanceTrace("module_render", { module: currentModule }).then((activeTrace) => {
      if (!activeTrace) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          activeTrace.stop();
        });
      });
    });
  }, [currentModule]);

  const openProspectInClientMaster = useCallback((prospect) => {
    setClientDraft({ token: Date.now(), prospectId: prospect.id, prospect });
    setCurrentModule("clients");
  }, []);

  const clearClientDraft = useCallback(() => setClientDraft(null), []);

  const completeProspectConversion = useCallback(async (clientId, client, prospectId) => {
    await updateDoc(doc(db, "client_communication_prospects", prospectId), {
      status: "Acquired",
      acquiredClientId: clientId,
      acquiredClientName: client.name || "",
      acquiredAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }, []);

  if (loading) return <ModuleLoader label="Loading Portal..." />;
  if (window.location.pathname === "/finishSignIn") return <Suspense fallback={<ModuleLoader />}><FinishSignIn /></Suspense>;
  if (!user) return (
    <Suspense fallback={<ModuleLoader label="Loading login..." />}>
      {showRegister ? <RegisterByCompany onDone={() => setShowRegister(false)} /> : <LoginForm onShowRegister={() => setShowRegister(true)} />}
    </Suspense>
  );

  const userDisplayName = userDoc?.displayName || user.displayName || user.email;
  const isSuper = isElevatedRole(userDoc?.role);

  const renderCurrentModule = () => {
    switch (currentModule) {
      case "assessments":
        return <LegacyModuleFrame src="/legacy/TaxManager.html" title="Tax Management" bridge="tax-clients" clients={clientDirectory} dropdownMasters={dropdownMasters} />;
      case "tasks":
        return <LegacyModuleFrame src="/legacy/TaskManager.html" title="Task Manager" bridge="task-users" users={userDirectory} clients={clientDirectory} dropdownMasters={dropdownMasters} currentUser={{ id: user.uid, uid: user.uid, ...userDoc, email: user.email, displayName: userDisplayName }} />;
      case "hr":
        return <LegacyModuleFrame src="/legacy/HRSuite.html" title="HR Suite" bridge="hr-autologin" dropdownMasters={dropdownMasters} currentUser={{ ...userDoc, email: user.email, displayName: userDisplayName }} />;
      case "visit":
        return <LegacyModuleFrame src="/legacy/VisitManager.html" title="Visit Manager" bridge="visit-manager" clients={clientDirectory} dropdownMasters={dropdownMasters} />;
      case "documents":
        return <LegacyModuleFrame src="/legacy/DocumentTracker.html" title="Document Tracker" bridge="document-tracker" clients={clientDirectory} users={userDirectory} dropdownMasters={dropdownMasters} />;
      case "clientdocs":
        return <div className="module-container"><ClientDocuments clients={clientDirectory} currentUser={{ id: user.uid, uid: user.uid, ...userDoc, email: user.email, displayName: userDisplayName }} /></div>;
      case "communications":
        return <div className="module-container"><ClientCommunications clients={clientDirectory} users={userDirectory} currentUser={{ id: user.uid, uid: user.uid, ...userDoc, email: user.email, displayName: userDisplayName }} onConvertProspect={openProspectInClientMaster} /></div>;
      case "billing":
        return <div className="module-container"><BillingModule clients={clientDirectory} dropdownMasters={dropdownMasters} currentUser={{ id: user.uid, uid: user.uid, ...userDoc, email: user.email, displayName: userDisplayName }} /></div>;
      case "receipts":
        return <div className="module-container"><BillingModule mode="receipts" clients={clientDirectory} dropdownMasters={dropdownMasters} currentUser={{ id: user.uid, uid: user.uid, ...userDoc, email: user.email, displayName: userDisplayName }} /></div>;
      case "billingreports":
        return <div className="module-container"><BillingModule mode="reports" clients={clientDirectory} dropdownMasters={dropdownMasters} currentUser={{ id: user.uid, uid: user.uid, ...userDoc, email: user.email, displayName: userDisplayName }} /></div>;
      case "roc":
        return <RocIntelligence currentUser={{ id: user.uid, uid: user.uid, ...userDoc, email: user.email, displayName: userDisplayName }} />;
      case "mis":
        return <LegacyModuleFrame src="/legacy/MIS.html" title="MIS" bridge="mis" users={userDirectory} clients={clientDirectory} dropdownMasters={dropdownMasters} currentUser={{ id: user.uid, uid: user.uid, ...userDoc, email: user.email, displayName: userDisplayName }} />;
      case "worklog":
        return <div className="module-container"><WorkLog userRole={userDoc?.role} /></div>;
      case "stationery":
        return <div className="module-container"><StationeryModule userRole={userDoc?.role} /></div>;
      case "admin":
        return <div className="module-container"><AdminPanel /></div>;
      case "backup":
        return <BackupModule currentUser={{ id: user.uid, uid: user.uid, ...userDoc, email: user.email, displayName: userDisplayName }} />;
      case "vermalog":
        return <div className="module-container"><VermaLog /></div>;
      case "masters":
        return <div className="module-container"><MasterPortal userRole={userDoc?.role} /></div>;
      case "clients":
        return <div className="module-container"><ClientMaster userRole={userDoc?.role} initialClientDraft={clientDraft} onDraftConsumed={clearClientDraft} onClientCreated={completeProspectConversion} /></div>;
      default:
        return renderHub();
    }
  };

  const hubCards = [
    ["worklog", "Timesheet", "Self tracking and timesheet", "01", FaClipboardList, "blue"],
    ["clients", "Client Master", "Main client database", "02", FaUsers, "green"],
    ["assessment", "Tax Management", "Tax matters, appeals and due dates", "03", FaFileAlt, "purple", "assessments"],
    ["documents", "Document Tracker", "Inward, tasks and reminders", "04", FaFolderOpen, "orange"],
    ["clientdocs", "Client Documents", "Document acknowledgements from Client Master", "05", FaFileAlt, "gold"],
    ["communications", "Client Communications", "Calls, emails, meetings and follow-ups", "06", FaComments, "blue"],
    ["billing", "Billing", "Bills, receipt register and reports", "07", FaFileInvoiceDollar, "teal"],
    ["roc", "ROC Intelligence", "Company intelligence and audit tracking", "10", FaChartBar, "violet"],
    ["visit", "Visit Manager", "Visits, networks and follow-ups", "11", FaMapMarkerAlt, "teal"],
    ["mis", "MIS", "Daily office updates and reports", "12", FaChartBar, "blue"],
    ["tasks", "Task Manager", "Office work and deadlines", "13", FaClipboardCheck, "red"],
    ["hr", "HR Suite", "Employees, leave and payroll", "14", FaUsers, "green"],
    ["stationery", "Stationery", "Inventory and usage", "15", FaBoxOpen, "gold"],
    ["masters", "Dropdown Masters", "Manage module dropdown values", "16", FaSlidersH, "blue"],
    ["backup", "Backup", "Offline ZIP backup and viewer", "17", FaArchive, "teal"],
    ["vermalog", "Verma Log", "Last seven days of system activity", "18", FaHistory, "red"],
    ["admin", "System Admin", "User roles and access", "19", FaShieldAlt, "purple"],
  ];

  function renderHub() {
    return (
      <div className="dashboard-layout">
        <div className="dashboard-modules">
          <div className="hub-grid">
            {hubCards.map(([accessKey, title, subtitle, , Icon, tone, moduleKey]) => {
              if (accessKey === "admin" && !isSuper) return null;
              if (accessKey !== "admin" && !hasAccess(accessKey)) return null;
              return (
                <button key={accessKey} className={`hub-card tone-${tone}`} onClick={() => setCurrentModule(moduleKey || accessKey)}>
                  <span className="hub-icon"><Icon /></span>
                  <div className="hub-card-copy">
                    <h3>{title}</h3>
                    <p>{subtitle}</p>
                  </div>
                </button>
              );
            })}
          </div>
          
        </div>
      </div>
    );
  }

  const topTitle =
    currentModule === "hub" ? "Dashboard" :
    currentModule === "assessments" ? "Tax Management" :
    currentModule === "tasks" ? "Task Manager" :
    currentModule === "hr" ? "HR Suite" :
    currentModule === "visit" ? "Visit Manager" :
    currentModule === "documents" ? "Document Tracker" :
    currentModule === "clientdocs" ? "Client Documents" :
    currentModule === "communications" ? "Client Communications" :
    currentModule === "billing" ? "Billing" :
    currentModule === "receipts" ? "Receipt Register" :
    currentModule === "billingreports" ? "Billing Reports" :
    currentModule === "roc" ? "ROC Intelligence" :
    currentModule === "mis" ? "MIS" :
    currentModule === "worklog" ? "Timesheet" :
    currentModule === "clients" ? "Client Master" :
    currentModule === "stationery" ? "Stationery" :
    currentModule === "masters" ? "Dropdown Masters" :
    currentModule === "backup" ? "Backup" :
    currentModule === "vermalog" ? "Verma Log" :
    currentModule === "admin" ? "System Admin" :
    "Office Management Portal";

  return (
    <div className="tax-shell">
      <section className="tax-main">
        <header className="tax-topbar">
          <div className="topbar-left">
            {currentModule !== "hub" && (
              <button className="dashboard-btn" onClick={() => setCurrentModule("hub")}>
                Home
              </button>
            )}
            {currentModule === "hub" && (
              <div className="brand-cluster" aria-label="Varma & Varma">
                <img src="/assets/varma-logo.png?v=2" alt="Varma & Varma Chartered Accountants" className="brand-main-logo" />
              </div>
            )}
          </div>
          <div className="topbar-title">
            <h1>{currentModule === "hub" ? "Office Management Software" : topTitle}</h1>
            
          </div>
          <div className="header-right">
            <span className="user-avatar"><FaUserCircle /></span>
            <span className="user-pill"><strong>{userDisplayName}</strong><small>{isSuper ? "Administrator" : userDoc?.role || "User"}</small></span>
            <button onClick={handleLogout} className="logout-btn">Logout</button>
          </div>
        </header>

        <main className={`${["assessments", "tasks", "hr", "visit", "documents", "roc", "mis"].includes(currentModule) ? "legacy-main" : "tax-content"} module-${currentModule}`}>
          <Suspense fallback={<ModuleLoader />}>{renderCurrentModule()}</Suspense>
        </main>
      </section>

      <style>{`
        .tax-shell { --primary:#08285A; --secondary:#2563EB; --bg:#F8FAFC; --soft:#F3F6FB; --surface:#FFFFFF; --text:#071F49; --muted:#566887; --line:#E4EAF3; min-height:100vh; background:linear-gradient(180deg,#FFFFFF 0%,#F7FAFF 100%); color:var(--text); }
        .tax-main { min-height:100vh; width:100%; display:flex; flex-direction:column; }
        .tax-topbar { min-height:96px; background:rgba(255,255,255,.94); border:1px solid var(--line); border-radius:0 0 16px 16px; display:flex; align-items:center; justify-content:center; gap:18px; padding:12px 32px; position:sticky; top:0; z-index:20; box-shadow:0 12px 28px rgba(15,31,73,.07); backdrop-filter:blur(10px); }
        .topbar-left { position:absolute; left:32px; display:flex; align-items:center; gap:16px; min-width:0; }
        .brand-cluster { display:flex; align-items:center; gap:14px; height:66px; }
        .brand-main-logo { height:60px; width:auto; object-fit:contain; display:block; border-radius:8px; }
        .topbar-title { margin:0; width:min(720px, calc(100% - 560px)); text-align:center; font-family:Inter,system-ui,-apple-system,'Segoe UI',sans-serif; color:var(--text); }
        .topbar-title h1 { margin:0; font-size:26px; font-weight:900; letter-spacing:-.01em; color:#08285A; }
        .topbar-title p { margin:6px 0 0; color:#64748B; font-size:15px; font-weight:600; }
        .dashboard-btn { background:#EFF6FF; color:var(--primary); border:1px solid #BFDBFE; border-radius:8px; padding:9px 14px; font-weight:800; cursor:pointer; }
        .dashboard-btn:hover { background:#DBEAFE; }
        .tax-content { padding:24px 40px 34px; position:relative; }
        .legacy-main { flex:1; min-height:calc(100vh - 86px); padding:0; background:var(--bg); }
        .legacy-main.module-roc { height:calc(100vh - 122px); min-height:0; overflow:hidden; background:linear-gradient(90deg,#061B3A 0 270px,#F5F8FC 270px); }
        .legacy-frame { width:100%; height:calc(100vh - 86px); border:0; display:block; background:var(--bg); }
        .legacy-loading { padding:32px; color:var(--muted); }
        .header-right { position:absolute; right:32px; display:flex; justify-content:flex-end; align-items:center; gap:14px; }
        .user-avatar { width:44px; height:44px; border-radius:50%; display:inline-flex; align-items:center; justify-content:center; background:linear-gradient(135deg,#0B5BC4,#0F73DC); color:#fff; font-size:24px; box-shadow:0 10px 20px rgba(37,99,235,.22); }
        .user-pill { color:var(--text); font-size:14px; max-width:240px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; display:flex; flex-direction:column; line-height:1.25; }
        .user-pill small { color:var(--muted); font-size:12px; font-weight:600; }
        .logout-btn { background:var(--surface); color:var(--primary); border:1px solid #BFDBFE; padding:8px 14px; border-radius:8px; cursor:pointer; font-weight:700; font-size:13px; }
        .module-container { max-width:1280px; margin:0 auto; width:100%; }
        .btn-back-floating { position:absolute; left:0; top:50%; transform:translateY(-50%); background:#fff; border:1px solid #d0cfd4; color:#3e3d48; cursor:pointer; font-weight:700; padding:8px 15px; border-radius:8px; display:flex; align-items:center; font-size:14px; box-shadow:0 2px 12px rgba(15,30,53,.10); }
        .dashboard-layout { min-height:calc(100vh - 154px); max-width:1540px; margin:0 auto; }
        .dashboard-modules { min-width:0; }
        .hub-footer { margin:18px auto 0; display:flex; align-items:center; justify-content:center; gap:10px; color:#71809D; font-size:14px; font-weight:700; }
        .page-header { margin-bottom:20px; text-align:left; min-height:auto; display:flex; align-items:center; justify-content:flex-start; }
        .page-header h2 { font-family:Inter,system-ui,-apple-system,'Segoe UI',sans-serif; font-size:32px; margin:8px 0 6px; color:var(--text); }
        .page-header p { color:var(--muted); margin:0; font-size:16px; }
        .hub-grid { display:grid; grid-template-columns:repeat(4,minmax(220px,1fr)); gap:18px; padding:0; margin-top:0; }
        .hub-card { min-height:142px; position:relative; display:grid; grid-template-columns:88px 1fr; align-items:center; gap:18px; padding:22px; border-radius:18px; text-align:left; background:rgba(255,255,255,.96); cursor:pointer; border:1px solid var(--line); box-shadow:0 12px 28px rgba(15,31,73,.06); transition:transform .2s, box-shadow .2s, border-color .2s; overflow:hidden; }
        .hub-card:hover { transform:translateY(-3px); box-shadow:0 20px 44px rgba(15,31,73,.12); border-color:#C7D7F5; }
        .hub-icon { width:76px; height:76px; border-radius:18px; display:inline-flex; align-items:center; justify-content:center; background:var(--tone-soft); color:var(--tone); font-size:38px; box-shadow:inset 0 0 0 1px rgba(255,255,255,.55); }
        .hub-card-copy { min-width:0; }
        .hub-card h3 { margin:0 0 8px; color:#071F49; font-size:19px; font-family:Inter,system-ui,-apple-system,'Segoe UI',sans-serif; font-weight:900; letter-spacing:-.01em; }
        .hub-card p { margin:0; color:#405272; font-size:15px; line-height:1.45; font-weight:500; }
        .tone-blue { --tone:#2563D6; --tone-soft:#EAF2FF; }
        .tone-green { --tone:#16A162; --tone-soft:#E8F8EF; }
        .tone-purple { --tone:#7C3AED; --tone-soft:#F1EAFF; }
        .tone-orange { --tone:#D97706; --tone-soft:#FFF1E2; }
        .tone-violet { --tone:#6D55D8; --tone-soft:#F0ECFF; }
        .tone-teal { --tone:#0E8EA5; --tone-soft:#E7F8FA; }
        .tone-red { --tone:#E13D5C; --tone-soft:#FDECEF; }
        .tone-gold { --tone:#D8A116; --tone-soft:#FFF5D9; }
        @media (max-width: 760px) {
          .tax-topbar { padding:12px 16px; align-items:center; flex-direction:column; border-radius:0 0 14px 14px; }
          .topbar-left, .header-right { position:static; width:100%; justify-content:center; }
          .brand-cluster { height:48px; }
          .brand-main-logo { height:44px; }
          .topbar-title { width:100%; order:-1; }
          .topbar-title h1 { font-size:22px; }
          .topbar-title p { font-size:14px; }
          .tax-content { padding:20px 14px 32px; }
          .hub-grid { grid-template-columns:1fr; padding:20px 0; }
          .hub-card { min-height:140px; grid-template-columns:74px 1fr; border-radius:16px; }
          .hub-icon { width:64px; height:64px; font-size:30px; }
          .user-pill { max-width:160px; }
          .page-header { display:block; min-height:0; }
        }
        @media (min-width: 761px) and (max-width: 1180px) {
          .hub-grid { grid-template-columns:repeat(2,minmax(260px,1fr)); }
          .topbar-title { width:min(520px, calc(100% - 460px)); }
          .hub-card { min-height:150px; }
        }
      `}</style>
    </div>
  );
}

export default App;
